import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useCreateResponseTask } from '../../../../../../services/task/mutations';
import {
  Task,
  TaskResponse,
  TaskState
} from '../../../../../../gql/graphql';
import { TaskContext } from '../Insurance';
import { useGetTaskResponsesByTask } from '../../../../../../services/task/querys';
import { Form } from 'antd';
import CardUploadResponse from 'components/Atoms/Cards/CardUploadResponse/CardUploadResponse';
import { LimitFiles } from '@common/utils/constants/limit-files-upload';
import { useSequentialFileUpload } from '@common/hooks/useSequentialFileUpload';

interface Props {
  onModalFinish: Function;
  disabled: boolean;
}

const Documents = ({ onModalFinish, disabled }: Props) => {
  const [form] = Form.useForm();
  const task: Task | any = useContext(TaskContext);

  const { data: taskResponses, reexecute: getTaskResponses } = useGetTaskResponsesByTask({
    variables: { findResponsesByTask: { taskId: task._id } }
  });
  const { fetching, execute: createResponseTask } = useCreateResponseTask();

  const [responseInfo, setResponseInfo] = useState<TaskResponse | any>();
  const [responseDoc, setResponseDoc] = useState<TaskResponse | any>();
  const [localFiles, setLocalFiles] = useState<any[]>([]);

  // Sequential upload queue — one file at a time to respect backend limits
  const uploadQueue = useSequentialFileUpload({
    buildPayload: (file, documentId) => ({
      _id: documentId ?? responseDoc?._id,
      task: task._id,
      key: 'insurance-doc',
      label: 'I want to upload my own insurance docs',
      step: 2,
      metadata: {},
      state: TaskState.Review,
      files: [file]
    }),
    onUploadFile: createResponseTask,
    extractCreatedId: (result: any) => result?.data?.createResponseTask?._id
  });

  useEffect(() => {
    getTaskResponses({ requestPolicy: 'network-only' });
  }, []);

  useEffect(() => {
    if (responseInfo && responseInfo.state !== TaskState.RequestError)
      form.setFieldsValue(responseInfo.metadata);
  }, [responseInfo]);

  useEffect(() => {
    const _response = taskResponses?.find((item) => item.step === 2 && item.key === 'insurance-information');
    setResponseInfo(_response);

    const _response2 = taskResponses?.find((item) => item.step === 2 && item.key === 'insurance-doc');
    setResponseDoc(_response2);
  }, [taskResponses]);

  // Sync localFiles with backend files when the queue is not running
  useEffect(() => {
    if (!uploadQueue.isProcessing) {
      setLocalFiles(responseDoc?.files || []);
    }
  }, [responseDoc, uploadQueue.isProcessing]);

  const uploadQueueFilesWithRetry = useMemo(() => {
    return uploadQueue.files.map(f => ({
      ...f,
      onRetry: () => uploadQueue.retryFile(f.id)
    }));
  }, [uploadQueue.files, uploadQueue.updateCount]);

  const onSubmitDoc = async (files: any[]) => {
    // Only process files not yet in the backend or in the queue
    const newFiles = files.filter((f: any) => !f.key && !f.queueId && !f.isTemporary);

    if (newFiles.length > 0) {
      const fileStates = uploadQueue.addFiles(newFiles);
      const tempFiles = fileStates.map(fs => ({
        name: fs.file.name,
        size: fs.file.size,
        queueId: fs.id,
        isTemporary: true
      }));

      // Replace local files with temp placeholders so the UI shows per-file queue status
      setLocalFiles(prev => [
        ...(prev?.filter((f: any) => f.key || f.isTemporary) || []),
        ...tempFiles
      ]);

      await uploadQueue.startQueue(fileStates);
      // All files done — refresh backend state then show the modal
      await getTaskResponses({ requestPolicy: 'network-only' });
      onModalFinish();
    }
  };

  // Locked once the borrower submits for review; only re-opens on request_error
  const isUploadDisabled = disabled || responseDoc?.state === TaskState.Review || responseDoc?.state === TaskState.Approved;

  // Rejected files should not appear in the card
  const displayFiles = localFiles.filter((f: any) => f.state !== 'request_error');

  return (
    <div className='mt-30'>
      <CardUploadResponse
        title={'Insurance documents'}
        limitFiles={(isUploadDisabled && !uploadQueue.isProcessing) ? 0 : LimitFiles.MULTIPLE_FILES}
        files={displayFiles}
        state={responseDoc?.state}
        uploadQueueFiles={uploadQueueFilesWithRetry}
        uploadQueue={uploadQueue}
        onChangeFiles={(files: any[]) => setLocalFiles(files)}
        submit={{
          title: 'Submit for review',
          loading: fetching || uploadQueue.isProcessing,
          modalConfirm: true,
          disabled: isUploadDisabled,
          confirmDescription:
            "After submitting, you'll have to contact us to make any changes.",
          onClick: (files) => onSubmitDoc(files as any[])
        }}
      />
    </div>
  );
};
export default Documents;